import { TURNSTILE_SITE_KEY } from '@xver/constants/env';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoundTurnstileObject, TurnstileProps } from 'react-turnstile';
import { Turnstile } from 'react-turnstile';

interface TurnstileWithSkeletonProps extends Omit<TurnstileProps, 'sitekey'> {
    skeletonHeight?: string;
    errorMessage?: string;
    retryLabel?: string;
}

export default function TurnstileWithSkeleton({
    skeletonHeight = '65px',
    errorMessage,
    retryLabel,
    onLoad,
    onVerify,
    onError,
    onExpire,
    ...turnstileProps
}: TurnstileWithSkeletonProps) {
    const { t } = useTranslation('validation');
    const [loaded, setLoaded] = useState(false);
    const [errored, setErrored] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const boundRef = useRef<BoundTurnstileObject | null>(null);

    const handleRetry = () => {
        setErrored(false);
        setLoaded(false);
        if (boundRef.current) {
            boundRef.current.reset();
        } else {
            setRetryKey((k) => k + 1);
        }
    };

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ position: 'relative', width: '100%', minHeight: skeletonHeight }}>
                {!loaded && !errored && (
                    <div
                        className="xver-serverless-skeleton"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            height: skeletonHeight,
                            borderRadius: '0',
                            zIndex: 1,
                            pointerEvents: 'none',
                        }}
                    />
                )}
                <Turnstile
                    key={retryKey}
                    sitekey={TURNSTILE_SITE_KEY}
                    fixedSize
                    onLoad={(widgetId, boundTurnstile) => {
                        boundRef.current = boundTurnstile;
                        setLoaded(true);
                        setErrored(false);
                        onLoad?.(widgetId, boundTurnstile);
                    }}
                    onVerify={(token, boundTurnstile) => {
                        onVerify?.(token, boundTurnstile);
                    }}
                    onError={(error, boundTurnstile) => {
                        setErrored(true);
                        onError?.(error, boundTurnstile);
                    }}
                    onExpire={(token, boundTurnstile) => {
                        onExpire?.(token, boundTurnstile);
                    }}
                    {...turnstileProps}
                />
            </div>
            {errored && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '6px',
                        padding: '8px',
                        background: 'rgba(235, 87, 87, 0.08)',
                        border: '1px solid rgba(235, 87, 87, 0.35)',
                        borderRadius: '4px',
                    }}
                >
                    <span style={{ fontSize: '12px', color: 'var(--color-error)' }}>
                        {errorMessage ?? t('turnstile.failedToLoad')}
                    </span>
                    <button
                        type="button"
                        onClick={handleRetry}
                        style={{
                            width: '100%',
                            fontSize: '12px',
                            color: 'var(--fg)',
                            background: 'transparent',
                            border: '1px solid rgba(235, 87, 87, 0.70)',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            cursor: 'pointer',
                        }}
                    >
                        {retryLabel ?? t('turnstile.retry')}
                    </button>
                </div>
            )}
        </div>
    );
}
